import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
// ────────────────────────────────────────────────────────────────────────────
// Query keys
// ────────────────────────────────────────────────────────────────────────────
export const cosKeys = {
    catalog: ['cosmetics', 'catalog'],
    inventory: (playerId) => ['cosmetics', 'inventory', playerId],
    equipped: (playerId) => ['cosmetics', 'equipped', playerId],
};
// ────────────────────────────────────────────────────────────────────────────
// Queries
// ────────────────────────────────────────────────────────────────────────────
export function useCosmetics() {
    return useQuery({
        queryKey: cosKeys.catalog,
        queryFn: async () => {
            const { data, error } = await supabase
                .from('cosmetics')
                .select('*')
                .order('unlock_level', { ascending: true, nullsFirst: false })
                .order('name', { ascending: true });
            if (error)
                throw error;
            return (data ?? []);
        },
        staleTime: 10 * 60_000,
    });
}
export function usePlayerCosmetics(playerId) {
    return useQuery({
        queryKey: playerId ? cosKeys.inventory(playerId) : ['cosmetics', 'inventory', 'none'],
        enabled: !!playerId,
        queryFn: async () => {
            if (!playerId)
                return [];
            const { data, error } = await supabase
                .from('player_cosmetics')
                .select('*')
                .eq('player_id', playerId);
            if (error)
                throw error;
            return (data ?? []);
        },
        staleTime: 30_000,
    });
}
/** Map des cosmétiques équipés d'un joueur (clé = type). 0-4 entrées. */
export function useEquippedCosmetics(playerId) {
    return useQuery({
        queryKey: playerId ? cosKeys.equipped(playerId) : ['cosmetics', 'equipped', 'none'],
        enabled: !!playerId,
        queryFn: async () => {
            if (!playerId)
                return {};
            const { data, error } = await supabase.rpc('get_equipped_cosmetics', { p_player_id: playerId });
            if (error)
                throw error;
            const map = {};
            for (const row of (data ?? [])) {
                map[row.type] = {
                    type: row.type,
                    cosmetic_id: row.cosmetic_id,
                    code: row.code,
                    name: row.name,
                    reward_meta: row.reward_meta ?? {},
                };
            }
            return map;
        },
        staleTime: 30_000,
    });
}
/** Raccourci : cosmétiques équipés du joueur courant. */
export function useMyEquippedCosmetics() {
    const { user } = useAuth();
    return useEquippedCosmetics(user?.id);
}
// ────────────────────────────────────────────────────────────────────────────
// Mutations
// ────────────────────────────────────────────────────────────────────────────
function invalidate(qc, playerId) {
    if (!playerId)
        return;
    void qc.invalidateQueries({ queryKey: cosKeys.inventory(playerId) });
    void qc.invalidateQueries({ queryKey: cosKeys.equipped(playerId) });
}
export function useEquipCosmetic() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (cosmeticId) => {
            const { error } = await supabase.rpc('equip_cosmetic', { p_cosmetic_id: cosmeticId });
            if (error)
                throw error;
        },
        onSuccess: () => invalidate(qc, user?.id),
    });
}
export function useUnequipCosmeticType() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (type) => {
            const { error } = await supabase.rpc('unequip_cosmetic_type', { p_type: type });
            if (error)
                throw error;
        },
        onSuccess: () => invalidate(qc, user?.id),
    });
}
export function useCheckMyCosmetics() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async () => {
            const { data, error } = await supabase.rpc('check_my_cosmetics');
            if (error)
                throw error;
            return (data ?? []);
        },
        onSuccess: (newly) => {
            if (newly.length > 0)
                invalidate(qc, user?.id);
        },
    });
}
// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────
/**
 * Extrait la couleur d'une bordure cosmétique. Notre seed stocke des hex
 * dans reward_meta.color. Fallback sur les couleurs par défaut.
 */
export function borderColorOf(equipped) {
    if (!equipped || equipped.type !== 'border')
        return undefined;
    const meta = equipped.reward_meta;
    return meta.color;
}
